/**
 * initShell — 工作台外壳初始化（纯 DOM 编排 + Mithril 挂载子组件）
 *
 * 结构：
 *   #app
 *   ├─ #header（顶栏，保持全宽）
 *   ├─ #taro-shell.TaroShell（Grid）
 *   │   ├─ .TaroShell-nav        ← NavSidebar（Mithril）
 *   │   ├─ splitter--nav         ← 拖拽调整宽度/双击折叠
 *   │   ├─ .TaroShell-center     ← Flarum #content（reparent，Mithril mount 根）
 *   │   ├─ splitter--agent
 *   │   └─ .TaroShell-agent      ← SarahPanel（taro-ai，停靠模式）
 *   └─ #footer
 *
 * 手机端（<768px）：Grid 关闭，MobileOverlay 接管（☰ 抽屉 + 🤖 浮层）。
 * 幂等：#taro-shell 已存在时直接返回。
 */

import app from 'flarum/forum/app';
import NavSidebar from '../components/NavSidebar';
import MobileOverlay from '../components/MobileOverlay';
import { attachDragResize } from '../utils/dragResize';

// mithril 由 Flarum core 以全局 `m` 提供（与 taro-ai 相同模式，不显式 import）

const STORAGE_KEY = 'taroShell.ratios';
const MOBILE_BREAKPOINT = 768;

// 比例边界（百分比）
const NAV_MIN = 10, NAV_MAX = 40;
const AGENT_MIN = 15, AGENT_MAX = 45;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export function isMobile() {
  return window.innerWidth < MOBILE_BREAKPOINT;
}

export function loadRatios() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (p && p.nav > 0 && p.agent > 0) return p;
    }
  } catch (_) { /* noop */ }
  return { nav: 25, agent: 25 }; // 1:2:1
}

export function saveRatios(ratios) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ratios));
  } catch (_) { /* noop */ }
}

export function initShell() {
  if (document.getElementById('taro-shell')) return true;

  const content = document.getElementById('content');
  if (!content) return false;

  injectCriticalStyles();

  // 强制归零滚动（防止历史 session 带来的 scrollY 偏移导致三栏重叠页首）
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;

  // Flarum 1.8 真实结构：#app > MAIN.App-content > #content
  // 外壳插在 #content 的父容器内（App-content），保持 header/drawer 顺序不变
  const container = content.parentElement;
  if (!container) return false;

  const ratios = loadRatios();

  // ---------- 创建外壳 ----------
  const shell = document.createElement('div');
  shell.id = 'taro-shell';
  shell.className = 'TaroShell';

  const nav = document.createElement('aside');
  nav.id = 'taro-shell-nav';
  nav.className = 'TaroShell-nav';

  const splitterNav = document.createElement('div');
  splitterNav.className = 'TaroShell-splitter TaroShell-splitter--nav';
  splitterNav.title = '拖拽调整宽度 · 双击折叠';

  const center = document.createElement('main');
  center.id = 'taro-shell-center';
  center.className = 'TaroShell-center';

  const splitterAgent = document.createElement('div');
  splitterAgent.className = 'TaroShell-splitter TaroShell-splitter--agent';
  splitterAgent.title = '拖拽调整宽度 · 双击折叠';

  const agent = document.createElement('aside');
  agent.id = 'taro-shell-agent';
  agent.className = 'TaroShell-agent';
  agent.innerHTML = '<div class="TaroShell-agentPlaceholder"><i class="fas fa-robot"></i><span>萨拉面板加载中…</span></div>';

  shell.appendChild(nav);
  shell.appendChild(splitterNav);
  shell.appendChild(center);
  shell.appendChild(splitterAgent);
  shell.appendChild(agent);

  // reparent：在 #content 原位置插入外壳，再把 #content 搬进中栏
  container.insertBefore(shell, content);
  center.appendChild(content);

  // ---------- 挂载 Mithril 子组件 ----------
  m.mount(nav, NavSidebar);

  const mobileUI = document.createElement('div');
  mobileUI.className = 'TaroShell-mobileUI-wrapper';
  document.body.appendChild(mobileUI);
  m.mount(mobileUI, MobileOverlay);

  // ---------- 拖拽 ----------
  attachDragResize(splitterNav, {
    onDelta: (dx) => {
      ratios.nav = clamp(ratios.nav + (dx / shell.clientWidth) * 100, NAV_MIN, NAV_MAX);
      shell.classList.remove('TaroShell--navCollapsed');
      applyRatios(ratios);
    },
    onEnd: () => saveRatios(ratios),
    onToggleCollapse: () => {
      shell.classList.toggle('TaroShell--navCollapsed');
      saveRatios(ratios);
    },
  });

  // agent 分隔条方向相反（向左拖 = 变宽）
  // 注：不提供双击折叠——萨拉面板永久常驻不可关闭
  attachDragResize(splitterAgent, {
    onDelta: (dx) => {
      ratios.agent = clamp(ratios.agent - (dx / shell.clientWidth) * 100, AGENT_MIN, AGENT_MAX);
      applyRatios(ratios);
    },
    onEnd: () => saveRatios(ratios),
  });

  applyRatios(ratios);
  applyMode();

  // ---------- SarahPanel 停靠（桌面右栏）----------
  dockAgentPanel(shell);
  window.addEventListener('resize', onResize);

  document.body.classList.add('taroshell-active');
  document.documentElement.setAttribute('data-taro-shell', 'on');
  return true;
}

/** 应用三栏比例到 CSS 变量 */
export function applyRatios(ratios) {
  const shell = document.getElementById('taro-shell');
  if (!shell) return;
  shell.style.setProperty('--taro-nav-w', ratios.nav + '%');
  shell.style.setProperty('--taro-agent-w', ratios.agent + '%');
}

/** 桌面/手机布局切换 */
export function applyMode() {
  const shell = document.getElementById('taro-shell');
  if (!shell) return;
  const mobile = isMobile();
  shell.classList.toggle('TaroShell--mobile', mobile);
  shell.classList.toggle('TaroShell--desktop', !mobile);
  syncAgentDock(mobile);
}

/** 桌面：萨拉面板停靠右栏且常开；手机：还原浮层控制 */
function syncAgentDock(mobile) {
  const dock = document.getElementById('taro-shell-agent');
  const wrapper = document.querySelector('.SarahPanel-wrapper');
  const sarahState = app.sarahAi || null;

  if (!mobile && dock) {
    if (wrapper && wrapper.parentElement !== dock) dock.appendChild(wrapper);
    if (sarahState) sarahState.panelOpen = true; // 停靠 = 常开
  }
  // 手机端由 MobileOverlay 负责挂载进 sheet；panelOpen 由浮层控制
}

/** 等待 taro-ai 的面板 wrapper 出现后停靠（它可能在 shell 之后才挂载） */
function dockAgentPanel() {
  let attempts = 0;
  const timer = setInterval(() => {
    attempts++;
    const wrapper = document.querySelector('.SarahPanel-wrapper');
    const dock = document.getElementById('taro-shell-agent');
    const done = !!(wrapper && dock && !isMobile());

    if (done) {
      dock.appendChild(wrapper);
      const ph = dock.querySelector('.TaroShell-agentPlaceholder');
      if (ph) ph.remove();
      if (app.sarahAi) app.sarahAi.panelOpen = true;
    }

    if (done || attempts > 40) clearInterval(timer); // 最多等 4s
  }, 100);
}

let _resizeTimer = null;
function onResize() {
  if (_resizeTimer) clearTimeout(_resizeTimer);
  _resizeTimer = setTimeout(applyMode, 150);
}

/**
 * 关键样式 JS 注入。
 *
 * 原因：Flarum 编译产物的 ?v= 版本号不随 CSS 内容变化，而 nginx 对 assets
 * 下发 immutable 30 天强缓存 —— 改 CSS 后老访客在同一 URL 上拿不到新样式。
 * forum.js 的版本号每次重建都会变，所以布局关键规则在此兜底注入，
 * 与 workspace.less 中的同名规则保持同步。
 */
function injectCriticalStyles() {
  if (document.getElementById('taro-shell-critical')) return;
  const style = document.createElement('style');
  style.id = 'taro-shell-critical';
  style.textContent = [
    // ---------- 隐藏原侧栏 p02（覆盖所有可能的侧栏 DOM 形态） ----------
    // 注意：sideNavContainer 是 IndexPage/UserPage 等页面内容的父级容器，
    // 里面除了侧栏还有讨论列表等主内容 —— 只能隐藏其中的 .sideNav，不能隐藏容器本身！
    'body.taroshell-active .IndexPage-nav.sideNav,',
    'body.taroshell-active .sideNavContainer > .sideNav,',
    'body.taroshell-active .App-navigation .sideNav,',
    'body.taroshell-active .App-content > .sideNav,',
    'body.taroshell-active .IndexPage .sideNav,',
    'body.taroshell-active .sideNavContainer .sideNav { display: none !important; }',
    // sideNavContainer 保留可见，清掉给侧栏让位的 padding-left
    'body.taroshell-active .sideNavContainer { padding-left: 0 !important; }',
    // 彻底禁止页面滚动 + 移除 .scrolled 导致 header 回到 fixed
    'html.taroshell-active, html.taroshell-active body { overflow: hidden !important; }',
    // 桌面端视口高度锁：#app 固定视口（无视 scrollY），页首入流，三栏 = 剩余空间
    '@media (min-width: 768px) {',
    // !important 覆盖 Flarum forum.css 的 .App { position: relative !important }
    '  body.taroshell-active #app { position: fixed !important; top: 0 !important; left: 0 !important; width: 100vw !important; height: 100vh !important; min-height: 0 !important; padding-top: 0 !important; display: flex !important; flex-direction: column !important; overflow: hidden !important; }',
    // 页首从 fixed/absolute 浮层改为文档流内元素（否则三栏从视口顶开始与页首重叠）
    '  body.taroshell-active .App-drawer { position: relative; flex: 0 0 auto; height: auto; }',
    '  body.taroshell-active #header, body.taroshell-active .App-header { position: relative !important; top: auto !important; left: auto !important; right: auto !important; }',
    '  body.taroshell-active .App-content { flex: 1 1 0; min-height: 0; overflow: hidden; display: flex; flex-direction: column; }',
    '  body.taroshell-active .TaroShell { flex: 1 1 0; min-height: 0; }',
    // 左栏：TaroNav-scroll 是唯一滚动容器（与 workspace.less 同步）
    '  body.taroshell-active .TaroShell-nav { overflow: hidden; height: 100%; min-height: 0; }',
    '  body.taroshell-active .TaroShell-nav .TaroNav { height: 100%; min-height: 0; }',
    // 右栏：萨拉面板填满；输入区不被消息区挤掉
    '  body.taroshell-active .TaroShell-agent { overflow: hidden; height: 100%; min-height: 0; display: flex; flex-direction: column; }',
    '  body.taroshell-active .TaroShell-agent .SarahPanel-wrapper { position: static !important; z-index: auto; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }',
    '  body.taroshell-active .TaroShell-agent .SarahPanel { position: static !important; width: 100% !important; height: 100% !important; max-height: none !important; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; border-radius: 0 !important; box-shadow: none !important; animation: none !important; }',
    '  body.taroshell-active .TaroShell-agent .SarahPanel-header { flex: 0 0 auto; }',
    '  body.taroshell-active .TaroShell-agent .SarahPanel-messages { flex: 1 1 auto; min-height: 0; }',
    '  body.taroshell-active .TaroShell-agent .SarahPanel-input-area { flex: 0 0 auto; }',
    '}',
  ].join('\n');
  document.head.appendChild(style);
}
